import { ReleaseStage } from '../models/ReleaseStage';

export type StageDependencyNode = {
    stage: ReleaseStage;
    children: StageDependencyNode[];
};

const getStageTime = (stage: ReleaseStage): number =>
    Date.parse(stage.createdOn || '') || 0;

export const getStageDependencyForest = (
    stages: ReleaseStage[]
): StageDependencyNode[] => {
    const orderedStages = stages.slice().sort((left, right) => {
        return getStageTime(left) - getStageTime(right);
    });
    const nodes = new Map(
        orderedStages.map((stage) => [
            stage.releaseStageCorrelationId,
            { stage, children: [] } as StageDependencyNode,
        ])
    );
    const childStageIds = new Set<string>();

    orderedStages.forEach((stage, stageIndex) => {
        const dependencyIdentifiers = new Set(
            (stage.deployments ?? []).flatMap(
                (deployment) =>
                    deployment.dependentDeploymentUniqueIdentifiers ?? []
            )
        );
        if (dependencyIdentifiers.size === 0) {
            return;
        }

        const parentStage = orderedStages
            .slice(0, stageIndex)
            .reverse()
            .find(
                (candidate) =>
                    candidate.environment === stage.environment &&
                    candidate.deployments?.some((deployment) =>
                        dependencyIdentifiers.has(deployment.uniqueIdentifier)
                    )
            );
        if (!parentStage) {
            return;
        }

        nodes
            .get(parentStage.releaseStageCorrelationId)
            ?.children.push(nodes.get(stage.releaseStageCorrelationId)!);
        childStageIds.add(stage.releaseStageCorrelationId);
    });

    return orderedStages
        .filter((stage) => !childStageIds.has(stage.releaseStageCorrelationId))
        .map((stage) => nodes.get(stage.releaseStageCorrelationId)!);
};

const findStageDependencyPath = (
    node: StageDependencyNode,
    stage: ReleaseStage
): ReleaseStage[] | undefined => {
    if (node.stage === stage) {
        return [node.stage];
    }

    for (const child of node.children) {
        const childPath = findStageDependencyPath(child, stage);
        if (childPath) {
            return [node.stage, ...childPath];
        }
    }

    return undefined;
};

export const getStageDependencyPath = (
    stages: ReleaseStage[],
    stage: ReleaseStage
): ReleaseStage[] | undefined => {
    for (const root of getStageDependencyForest(stages)) {
        const path = findStageDependencyPath(root, stage);
        if (path) {
            return path;
        }
    }

    return undefined;
};

export const getStageDependencyRoot = (
    stages: ReleaseStage[],
    stage: ReleaseStage
): ReleaseStage | undefined => getStageDependencyPath(stages, stage)?.[0];
